import { HttpHeaders } from '@angular/common/http';

import { EnvUtils, ORANGE_APP_INFO_PROPERTIES, OrangeAppInfo, StoreService } from '@ecm/dispatcher';

import { AccessCode } from '../../enums/access-code.enum';
import { HttpMethod } from '../../enums/http-method.enum';
import { RequestOptions } from '../../enums/request-options';
import { TransactionInputs } from '../../enums/transaction-inputs';
import { BcoService } from '../../services/bco.service';

/**
 * Class delivering static methods used as helpers in ErableHttpService
 * All methods here are involved in the preparation of the Request
 */
export class RequestUtils {
    /**
     * Header name for the session ID (used for tracking)
     * @type {string}
     */
    private static readonly SESSION_ID_HEADER: string = 'X-Orange-Session-Id';

    /**
     * Header name for the Request ID (used for tracking)
     * @type {string}
     */
    private static readonly REQUEST_ID_HEADER: string = 'X-Orange-Request-Id';

    /**
     * Header name for the Origin ID
     * @type {string}
     */
    private static readonly ORIGIN_ID_HEADER: string = 'X-Orange-Origin-Id';

    /**
     * Header name for the Caller ID
     * @type {string}
     */
    private static readonly CALLER_ID_HEADER: string = 'X-Orange-Caller-Id';

    /**
     * Header name for the native app info (name and version of the Orange native app hosting the webview)
     * @type {string}
     */
    public static readonly APP_INFO_HEADER: string = 'x-orange-app-info';

    /**
     * Object values access keys
     * @type {string}
     */
    private static readonly ID_KEY: string = 'id';

    /**
     * Default X-Orange-Origin-Id value
     * @type {string}
     */
    private static readonly DEFAULT_ORIGIN_ID: string = 'ECQ';

    /**
     * Webview X-Orange-Origin-Id value
     * @type {string}
     */
    private static readonly WEBVIEW_ORIGIN_ID: string = 'ECQ_webview';

    /**
     * Returns an object containing the requestOptions and the BCO transactionId
     * @param {HttpMethod} method
     * @param {RequestOptions} options
     * @param {HttpHeaders} defaultHeaders default headers for all requests
     * @param {BcoService} bco service provided for tracking
     * @returns {Object} requestOptions and transactionId
     */
    public static prepareRequest(
        method: HttpMethod,
        options: RequestOptions,
        defaultHeaders: HttpHeaders,
        bcoService: BcoService,
    ): { requestOptions: any; transactionId: string } {
        // log transaction start in BCOService
        const transactionInputs: TransactionInputs = {
            httpMethod: method,
            feature: options.feature || 'undefined',
            functionality: options.functionality || 'undefined',
            pageId: options.pageId || 'undefined',
            partner: options.partner || 'undefined',
            service: options.service || 'undefined',
            originId: options.headers?.get(RequestUtils.ORIGIN_ID_HEADER) || '',
            consumerId: options.headers?.get(RequestUtils.CALLER_ID_HEADER) || '',
            sessionId: options.headers?.get(RequestUtils.SESSION_ID_HEADER) || '',
        };

        const transactionId = bcoService.beginTransaction(transactionInputs);

        // Don't set default Content-Type header if disableContentTypeHeader option is set
        if (options.disableContentTypeHeader) {
            defaultHeaders = defaultHeaders.delete('Content-Type');
        }

        // add custom headers to default headers
        const headers = RequestUtils.computeCustomHeaders(transactionId, defaultHeaders, options.headers);

        const responseType = RequestUtils.getResponseType(method, options);

        // set request options
        const requestOptions: any = {
            headers,
            withCredentials: true,
            responseType,
            reportProgress: false,
            observe: 'response',
        };

        if (options.params) {
            requestOptions.params = options.params;
        }

        return { requestOptions, transactionId };
    }

    /**
     * Return the responseType based on the Request Method and the responseType option
     * @param {HttpMethod} method HTTP method of the request
     * @param {RequestOptions} [options={}] Request options, only the responseType key is used here (optional)
     * @return string
     */
    public static getResponseType(method: HttpMethod, options: RequestOptions = {}): string {
        return method === HttpMethod.get ? options.responseType || 'json' : 'json';
    }

    /**
     * Get the default headers and add custom headers for BCO and headers provided to get or post functions
     * @param {string} transactionId
     * @param {HttpHeaders} default headers sent to all requests, generated in the constructor
     * @param {HttpHeaders} custom headers sent to the http-method called (get, post, put, ...)
     * @returns {HttpHeaders}
     */
    public static computeCustomHeaders(
        transactionId: string,
        defaultHeaders: HttpHeaders,
        headers?: HttpHeaders,
    ): HttpHeaders {
        // set request headers, based on default headers + session/transaction headers
        // append return a new instance, the calls must be chained
        let requestHeaders: HttpHeaders = defaultHeaders
            .append(RequestUtils.SESSION_ID_HEADER, StoreService.get('session')[RequestUtils.ID_KEY])
            .append(RequestUtils.REQUEST_ID_HEADER, transactionId);

        // Replace default headers by custom headers in function call
        if (!headers) return requestHeaders;

        for (const headerKey of headers.keys()) {
            requestHeaders = requestHeaders.set(headerKey, headers.get(headerKey));
        }

        return requestHeaders;
    }

    /**
     * Compute the `x-orange-app-info` header value from the native app info
     * Format : `name=<name>;version=<version>;os=<os>;os-ver=<os-ver>`, keeping only the available parts
     * @param {OrangeAppInfo} appInfo validated native app info, `null` when unavailable
     * @return {string} header value, `null` when no app info is available (the header must not be sent)
     */
    public static computeAppInfoHeaderValue(appInfo: OrangeAppInfo | null): string | null {
        if (!appInfo) {
            return null;
        }

        const parts: string[] = ORANGE_APP_INFO_PROPERTIES.filter((property) => appInfo[property]).map(
            (property) => `${property}=${appInfo[property]}`,
        );

        return parts.length ? parts.join(';') : null;
    }

    /**
     * Gets caller ID
     * @return {string}
     */
    public static getCallerId(): string {
        return EnvUtils.getBasicatCode() === 'SDI' ? AccessCode.SDI : AccessCode.ECQ;
    }

    /**
     * Returns X-Orange-Origin-Id value
     * @return {string}
     */
    public static getOriginId(): string {
        return EnvUtils.isWebview() ? RequestUtils.WEBVIEW_ORIGIN_ID : RequestUtils.DEFAULT_ORIGIN_ID;
    }
}
